import { Link } from "@tanstack/react-router";

import { CATEGORY_LABELS, readingMinutes, type Post } from "@/content/posts";

export function formatPostDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(Date.UTC(year!, (month ?? 1) - 1, day ?? 1)).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function CategoryChips({ active }: { active?: string }) {
  return (
    <ul className="mt-8 flex flex-wrap gap-3">
      {(Object.keys(CATEGORY_LABELS) as (keyof typeof CATEGORY_LABELS)[]).map((id) => (
        <li key={id}>
          <Link
            to="/blog/category/$category"
            params={{ category: id }}
            aria-current={active === id ? "page" : undefined}
            className={`inline-flex min-h-[44px] items-center rounded-full border px-4 font-mono text-eyebrow uppercase transition-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 ${
              active === id
                ? "border-brand-700 bg-brand-100 text-brand-800"
                : "border-ink-200 bg-paper-0 text-ink-600 hover:border-brand-600/40 hover:text-brand-800"
            }`}
          >
            {CATEGORY_LABELS[id]}
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function PostList({ posts }: { posts: Post[] }) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {posts.map((post) => (
        <article
          key={post.slug}
          className="flex h-full flex-col rounded-2xl border border-ink-200 bg-paper-0 p-6 transition-brand hover:border-brand-600/40 md:p-8"
        >
          <span className="inline-flex w-fit rounded-full bg-brand-100 px-3 py-1 font-mono text-eyebrow uppercase font-medium text-brand-800">
            {CATEGORY_LABELS[post.category]}
          </span>
          <h2 className="mt-5 font-display text-h3 text-ink-900">
            <Link
              to="/blog/$slug"
              params={{ slug: post.slug }}
              className="underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700"
            >
              {post.title}
            </Link>
          </h2>
          <p className="mt-3 text-body text-ink-600">{post.description}</p>
          <p className="mt-auto pt-6 font-mono text-eyebrow uppercase text-ink-500">
            {formatPostDate(post.publishedAt)} · {readingMinutes(post)} min read
          </p>
        </article>
      ))}
    </div>
  );
}

export default PostList;
